import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  LayoutDashboard,
  LogOut,
  Menu,
  Package2,
  PanelLeftClose,
  PanelRightClose,
  Search,
  ShieldCheck,
  Sparkles,
  Settings,
  Activity,
  ArrowRightLeft,
  Layers3,
  ScanLine,
  History,
  ShoppingCart,
  X,
  ChevronRight,
  Scale,
  ListTodo,
  PackageCheck,
} from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import api from "../api/api";
import { formatDateTime } from "../utils/datetime";
import { useDialog } from "./DialogProvider";
import { includesSearch } from "../utils/search";

function NotificationsMenu({ role, open, setOpen, loading, items, unseenCount, onRefresh }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [setOpen]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, setOpen]);

  const hasAny = items.length > 0;
  const roleLabel = role === "admin" ? "admin" : "staff";

  return (
    <div ref={rootRef} className="relative">
      <button
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-white/50 bg-white/40 backdrop-blur-sm text-slate-600 transition-all duration-200 hover:bg-white/60 hover:text-slate-900 hover:scale-105 active:scale-95"
        type="button"
        aria-label="Notifikasi"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-4 w-4" />
        {unseenCount > 0 ? (
          <span
            className="absolute -top-1.5 -right-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-r from-rose-500 to-pink-500 px-1.5 text-[10px] font-bold text-white shadow-lg shadow-rose-500/30 animate-notif-pulse"
            aria-label={`${unseenCount} notifikasi baru`}
          >
            {unseenCount > 99 ? "99+" : unseenCount}
          </span>
        ) : hasAny ? (
          <span
            className="absolute -top-0.5 -right-0.5 inline-flex h-2.5 w-2.5 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 ring-2 ring-white"
            aria-label="Ada notifikasi"
          />
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+8px)] z-40 w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Pemberitahuan</div>
              <div className="text-xs text-slate-500">Terlihat untuk role: {roleLabel}</div>
            </div>
            <button className="btn btn-secondary px-3" type="button" onClick={onRefresh} disabled={loading}>
              Refresh
            </button>
          </div>

          <div className="max-h-[60vh] overflow-auto">
            {loading ? <div className="px-4 py-4 text-sm text-slate-600">Memuat…</div> : null}

            {!loading && !items.length ? (
              <div className="px-4 py-6 text-sm text-slate-600">Belum ada pemberitahuan.</div>
            ) : null}

            {!loading && items.length ? (
              <div className="divide-y divide-slate-100">
                {items.map((n) => (
                  <div key={n.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">{n.title || "(Tanpa judul)"}</div>
                        <div className="mt-1 text-xs text-slate-500">{formatDateTime(n.created_at) || "-"}</div>
                      </div>
                      <div className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700">
                        {n.audience || "all"}
                      </div>
                    </div>
                    {n.message ? (
                      <div className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-slate-700">{n.message}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SearchGlobal({ isAdmin, className = "hidden w-full max-w-xl md:flex" }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && String(e.key || "").toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    const q = String(query || "").trim();
    if (!q) {
      setOrders([]);
      setProducts([]);
      setBundles([]);
      setLoading(false);
      return;
    }

    let alive = true;
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const searchCatalog = q.length >= 2;
        const [ordersRes, productsRes, bundlesRes] = await Promise.allSettled([
          api.get(`/api/orders/search?q=${encodeURIComponent(q)}&limit=20`),
          searchCatalog ? api.get("/products") : Promise.resolve({ data: [] }),
          searchCatalog ? api.get("/bom") : Promise.resolve({ data: [] }),
        ]);

        if (!alive) return;

        if (ordersRes.status === "fulfilled") {
          const orderRows = Array.isArray(ordersRes.value?.data) ? ordersRes.value.data : [];
          setOrders(orderRows.slice(0, 6));
        } else {
          setOrders([]);
        }

        if (productsRes.status === "fulfilled") {
          const allProducts = Array.isArray(productsRes.value?.data) ? productsRes.value.data : [];
          const productRows = allProducts
            .filter((p) => {
              const text = `${p?.kode_barang || ""} ${p?.nama_barang || ""}`;
              return includesSearch(text, q);
            })
            .slice(0, 6);
          setProducts(productRows);
        } else {
          setProducts([]);
        }

        if (bundlesRes.status === "fulfilled") {
          const raw = Array.isArray(bundlesRes.value?.data) ? bundlesRes.value.data : [];
          const grouped = new Map();
          raw.forEach((r) => {
            const id = Number(r?.bundle_id);
            if (!Number.isInteger(id) || id <= 0) return;
            if (!grouped.has(id)) {
              grouped.set(id, {
                bundle_id: id,
                bundle_name: String(r?.bundle_name || "").trim(),
              });
            }
          });

          const bundleRows = Array.from(grouped.values())
            .filter((b) => includesSearch(`${b.bundle_name} #${b.bundle_id}`, q))
            .slice(0, 4);
          setBundles(bundleRows);
        } else {
          setBundles([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }, 260);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query]);

  const hasResults = orders.length > 0 || products.length > 0 || bundles.length > 0;

  const buildHistoryPath = (term, focusOrderId) => {
    const params = new URLSearchParams();
    const q = String(term || "").trim();
    if (q) params.set("q", q);
    if (focusOrderId) params.set("focusOrderId", String(focusOrderId));
    params.set("autoscroll", "1");
    return `/history?${params.toString()}`;
  };

  const goTo = (path, nextQuery = "") => {
    navigate(path);
    setOpen(false);
    setQuery(nextQuery);
  };

  const submitFromEnter = () => {
    const q = String(query || "").trim();
    if (!q) return;

    if (orders.length > 0) {
      const first = orders[0];
      return goTo(buildHistoryPath(first.order_number || q, first.order_id));
    }

    if (products.length > 0) {
      const first = products[0];
      const target = isAdmin ? "/products" : "/stock-in";
      return goTo(target, String(first.kode_barang || q));
    }

    if (bundles.length > 0) {
      const first = bundles[0];
      const target = isAdmin ? "/bom-rule" : "/stock-out";
      return goTo(target, String(first.bundle_name || q));
    }

    return goTo(buildHistoryPath(q));
  };

  return (
    <div ref={rootRef} className={"relative " + className}>
      <div className="flex w-full items-center gap-2 rounded-xl border border-white/50 bg-white/50 backdrop-blur-md px-3 py-2 shadow-sm transition-all focus-within:bg-white/70 focus-within:border-slate-300 focus-within:shadow-md focus-within:shadow-slate-200/50">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          ref={inputRef}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitFromEnter();
            }
          }}
          className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
          placeholder="Cari transaksi, produk, bundle..."
        />
        <kbd className="hidden rounded-lg border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-500 sm:inline-flex">
          Ctrl K
        </kbd>
      </div>

      {open ? (
        <div className="absolute top-[calc(100%+8px)] z-40 max-h-[68vh] w-full overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-900/10">
          {loading ? <div className="px-3 py-3 text-xs text-slate-500">Mencari...</div> : null}

          {!loading && !hasResults && String(query || "").trim() ? (
            <div className="px-3 py-3 text-xs text-slate-500">Tidak ada hasil untuk "{query}".</div>
          ) : null}

          {!loading && !String(query || "").trim() ? (
            <div className="px-3 py-3 text-xs text-slate-500">Ketik kata kunci untuk mulai mencari.</div>
          ) : null}

          {orders.length > 0 ? (
            <div className="mb-2">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Transaksi</div>
              {orders.map((row) => (
                <button
                  key={row.order_id}
                  type="button"
                  onClick={() => goTo(buildHistoryPath(row.order_number || query, row.order_id))}
                  className="w-full rounded-xl px-3 py-2 text-left hover:bg-slate-50"
                >
                  <div className="truncate text-sm font-semibold text-slate-900">{row.order_number || row.order_code || `Order #${row.order_id}`}</div>
                  <div className="truncate text-xs text-slate-500">Resi: {row.resi_number || "-"} • Qty: {row.total_qty || 0}</div>
                </button>
              ))}
            </div>
          ) : null}

          {products.length > 0 ? (
            <div className="mb-2">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Produk</div>
              {products.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => goTo(isAdmin ? "/products" : "/stock-in", `${row.kode_barang || ""}`)}
                  className="w-full rounded-xl px-3 py-2 text-left hover:bg-slate-50"
                >
                  <div className="truncate text-sm font-semibold text-slate-900">{row.nama_barang || "(Tanpa nama)"}</div>
                  <div className="truncate font-mono text-xs text-slate-500">{row.kode_barang || "-"}</div>
                </button>
              ))}
            </div>
          ) : null}

          {bundles.length > 0 ? (
            <div>
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Bundle</div>
              {bundles.map((row) => (
                <button
                  key={row.bundle_id}
                  type="button"
                  onClick={() => goTo(isAdmin ? "/bom-rule" : "/stock-out", row.bundle_name || query)}
                  className="w-full rounded-xl px-3 py-2 text-left hover:bg-slate-50"
                >
                  <div className="truncate text-sm font-semibold text-slate-900">{row.bundle_name || "(Tanpa nama)"}</div>
                  <div className="truncate text-xs text-slate-500">Bundle #{row.bundle_id}</div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ─── REDESIGNED NAV COMPONENTS ─────────────────────────────────

function NavLinkItem({ to, label, icon: Icon, onClick, collapsed, index = 0 }) {
  const { pathname } = useLocation();
  const active = pathname === to || (to === "/products" && pathname.startsWith("/products/")) || (to === "/events" && pathname.startsWith("/events")) || (to === "/events" && pathname.startsWith("/kasir")) || (to === "/events" && pathname.startsWith("/rekap"));
  const labelClass = collapsed ? "truncate lg:hidden" : "truncate";

  return (
    <Link
      to={to}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={
        "group relative flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-colors duration-150 animate-nav-item " +
        (collapsed ? "lg:justify-center lg:gap-0 lg:px-2 " : "") +
        (active
          ? "bg-zinc-900 text-white shadow-sm"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900")
      }
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Active left accent bar */}
      {active && !collapsed ? (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-slate-400 animate-glow-pulse" />
      ) : null}

      {/* Icon with bounce */}
      <span
        className={
          "nav-icon-bounce inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200 " +
          (active
            ? "bg-white/20 text-white"
            : "text-slate-500 group-hover:text-slate-700")
        }
        aria-hidden="true"
      >
        <Icon className="h-[18px] w-[18px]" />
      </span>

      {/* Label with slide */}
      <span className={labelClass + " nav-text-slide"}>{label}</span>

      {/* Collapsed active dot */}
      {active && collapsed ? (
        <span className="absolute -right-0.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-slate-700 animate-glow-pulse hidden lg:block" />
      ) : null}
    </Link>
  );
}

function NavSection({ title, items, collapsed, onItemClick, startIndex = 0 }) {
  if (!items.length) return null;

  return (
    <div className="space-y-1">
      {/* Section header */}
      <div className={collapsed
        ? "flex items-center gap-2 px-3 pt-4 pb-1 lg:hidden"
        : "flex items-center gap-2 px-3 pt-4 pb-1"
      }>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
          {title}
        </span>
        <span className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Nav items */}
      <div className="grid gap-0.5">
        {items.map((item, idx) => (
          <NavLinkItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            collapsed={collapsed}
            onClick={onItemClick}
            index={startIndex + idx}
          />
        ))}
      </div>
    </div>
  );
}

// ─── REDESIGNED HEADER ─────────────────────────────────────────

function ShellHeader({ onToggleSidebar, sidebarCollapsed, mobileMenuOpen, setMobileMenuOpen, auth, onLogout }) {
  const { pathname } = useLocation();
  const { toast } = useDialog();

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const lastToastIdRef = useRef(0);
  const notifOpenRef = useRef(false);

  useEffect(() => {
    notifOpenRef.current = notifOpen;
  }, [notifOpen]);

  const refreshNotifs = useCallback(
    async ({ bootstrap = false } = {}) => {
      if (!auth.user) return;

      setNotifLoading(true);
      try {
        const res = await api.get("/notifications?limit=5");
        const rows = Array.isArray(res?.data) ? res.data : [];

        setNotifItems(rows);

        const maxId = rows.reduce((acc, r) => {
          const id = Math.trunc(Number(r?.id));
          return Number.isFinite(id) && id > acc ? id : acc;
        }, 0);

        if (bootstrap) {
          lastToastIdRef.current = maxId;
          setUnseenCount(0);
          return;
        }

        const prevMaxId = lastToastIdRef.current;
        if (maxId > prevMaxId) {
          const news = rows
            .filter((r) => Math.trunc(Number(r?.id)) > prevMaxId)
            .sort((a, b) => Math.trunc(Number(a?.id)) - Math.trunc(Number(b?.id)));

          news.forEach((n) => {
            const title = String(n?.title || "Pemberitahuan").trim() || "Pemberitahuan";
            const msg = String(n?.message || "").trim();
            const brief = msg.length > 160 ? msg.slice(0, 160) + "…" : msg;
            toast({
              type: "info",
              title,
              message: brief,
              durationMs: 3500,
              pauseOnHover: true,
            });
          });

          lastToastIdRef.current = maxId;

          if (!notifOpenRef.current) {
            setUnseenCount((prev) => Math.min(99, prev + news.length));
          }
        }
      } catch {
        // ignore
      } finally {
        setNotifLoading(false);
      }
    },
    [auth.user, toast]
  );

  useEffect(() => {
    if (!auth.user) return;
    refreshNotifs({ bootstrap: true });
    const t = setInterval(() => {
      refreshNotifs({ bootstrap: false });
    }, 60000);

    return () => clearInterval(t);
  }, [auth.user, refreshNotifs]);

  useEffect(() => {
    if (!notifOpen) return;
    setUnseenCount(0);
  }, [notifOpen]);

  const pageMeta = useMemo(() => {
    const map = {
      "/": { section: "Overview", title: "Dashboard" },
      "/stock-in": { section: "Inventory", title: "Stock Masuk" },
      "/stock-out": { section: "Inventory", title: "Stock Keluar" },
      "/history": { section: "Overview", title: "History" },
      "/stock-opname": { section: "Inventory", title: "Stock Opname" },
      "/products": { section: "Admin", title: "Products" },
      "/products/new": { section: "Admin", title: "Tambah Produk" },
      "/bom-rule": { section: "Admin", title: "Bundle" },
      "/activity": { section: "Admin", title: "Aktivitas" },
      "/admin": { section: "Admin", title: "Settings" },
      "/admin/approval": { section: "Admin", title: "Approval" },
      "/events": { section: "POS", title: "Event" },
      "/accountant/dashboard": { section: "Audit", title: "Dashboard Audit" },
      "/accountant/reconciliations": { section: "Audit", title: "Rekonsiliasi Kas" },
      "/accountant/notes": { section: "Audit", title: "Daftar Temuan" },
      "/accountant/stock": { section: "Audit", title: "Pantau Stok" },
    };

    if (pathname.startsWith("/events/")) return { section: "POS", title: "Detail Event" };
    if (pathname.startsWith("/kasir/")) return { section: "POS", title: "Kasir" };
    if (pathname.startsWith("/rekap/")) return { section: "POS", title: "Rekap Kasir" };

    return map[pathname] || { section: "Inventory", title: "Page" };
  }, [pathname]);

  return (
    <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white">
      <div className="flex items-center gap-3 px-4 py-2.5 lg:h-16 lg:px-6 lg:py-0">
        {/* Mobile menu button */}
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900 active:scale-95 lg:hidden"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label={mobileMenuOpen ? "Tutup menu" : "Buka menu"}
        >
          <Menu className="h-4 w-4" />
        </button>

        {/* Desktop sidebar toggle */}
        <button
          type="button"
          className="hidden h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:bg-slate-50 hover:text-slate-900 active:scale-95 lg:inline-flex"
          onClick={onToggleSidebar}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? <PanelRightClose className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>

        {/* Breadcrumb title */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="font-medium text-slate-400">{pageMeta.section}</span>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
            <span key={pathname} className="font-semibold text-slate-900 animate-fade-in">{pageMeta.title}</span>
          </div>
        </div>

        {/* Search */}
        <SearchGlobal isAdmin={auth.isAdmin} />

        {/* Notifications */}
        <NotificationsMenu
          role={auth.user?.role}
          open={notifOpen}
          setOpen={setNotifOpen}
          loading={notifLoading}
          items={notifItems}
          unseenCount={unseenCount}
          onRefresh={() => refreshNotifs({ bootstrap: false })}
        />

        {/* User profile pill */}
        <div className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 bg-white/80 backdrop-blur-sm px-2.5 py-1.5 shadow-sm transition-all hover:shadow-md sm:gap-3 sm:px-3">
          {/* Gradient ring avatar */}
          <div className="relative">
            <div className="absolute -inset-[2px] rounded-full bg-slate-400 opacity-80" />
            <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">
              {String(auth.user?.username || "U").slice(0, 1).toUpperCase()}
            </div>
          </div>
          <div className="hidden leading-tight sm:block">
            <div className="text-sm font-semibold text-slate-900">{auth.user?.username || "-"}</div>
            <div className="text-[11px] text-slate-500 capitalize">{auth.user?.role || "-"}</div>
          </div>
          <button onClick={onLogout} className="hidden h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-700 active:scale-95 sm:inline-flex">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* Mobile search */}
      <div className="px-4 pb-2.5 md:hidden">
        <SearchGlobal isAdmin={auth.isAdmin} className="flex w-full" />
      </div>
    </header>
  );
}

// ─── MAIN APP SHELL WITH DARK SIDEBAR ──────────────────────────

export function AppShell({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const auth = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen]);

  const onLogout = () => {
    auth.logout();
    navigate("/login", { replace: true });
  };

  const isKasir = auth.user?.role === "kasir";
  const isAccountant = auth.user?.role === "accountant";

  const navSections = [
    {
      title: "Overview",
      items: (isKasir || isAccountant) ? [] : [
        { to: "/", label: "Dashboard", icon: LayoutDashboard },
        { to: "/history", label: "History", icon: History },
      ],
    },
    {
      title: "Inventory",
      items: (isKasir || isAccountant) ? [] : [
        { to: "/stock-in", label: "Stock Masuk", icon: ArrowRightLeft },
        { to: "/stock-out", label: "Stock Keluar", icon: ShoppingCart },
        { to: "/stock-opname", label: "Stock Opname", icon: ScanLine },
      ],
    },
    {
      title: "Akuntansi & Audit",
      items: (auth.isAdmin || isAccountant) ? [
        { to: "/accountant/dashboard", label: "Ringkasan Akuntan", icon: Activity },
        { to: "/accountant/reconciliations", label: "Cek Setoran Kasir", icon: Scale },
        { to: "/accountant/notes", label: "Catatan Masalah", icon: ListTodo },
        { to: "/accountant/stock", label: "Pantau Stok", icon: PackageCheck },
      ] : []
    },
    {
      title: "POS",
      items: isAccountant ? [] : [
        { to: "/events", label: "POS Event", icon: CalendarDays },
      ],
    },
    {
      title: "Admin",
      items: auth.isAdmin
        ? [
            { to: "/admin", label: "Admin Settings", icon: Settings },
            { to: "/admin/approval", label: "Approval", icon: ShieldCheck },
            { to: "/products", label: "Products", icon: Package2 },
            { to: "/bom-rule", label: "Bundle", icon: Layers3 },
            { to: "/activity", label: "Aktivitas", icon: Activity },
          ]
        : [],
    },
  ];

  // Calculate staggered animation index across all sections
  let globalIndex = 0;
  const sectionsWithIndex = navSections.map((section) => {
    const startIndex = globalIndex;
    globalIndex += section.items.length;
    return { ...section, startIndex };
  });

  return (
    <div className="h-screen overflow-hidden bg-transparent text-slate-900">
      <div className="relative flex h-screen">
        {/* ─── DARK SIDEBAR ─── */}
        <aside
          className={
            "fixed top-0 left-0 z-40 flex h-screen w-[min(17rem,calc(100vw-1.5rem))] flex-col bg-zinc-50 border-r border-zinc-200 transition-[transform,width] duration-300 " +
            "lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 " +
            (sidebarCollapsed ? "lg:w-[72px]" : "lg:w-[264px]") +
            (mobileMenuOpen ? " translate-x-0" : " -translate-x-full lg:translate-x-0")
          }
        >
          {/* Branding */}
          <div className="flex h-16 items-center gap-3 border-b border-zinc-200 px-4 bg-zinc-50">
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-zinc-900 text-white shadow-sm">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className={sidebarCollapsed ? "min-w-0 flex-1 lg:hidden" : "min-w-0 flex-1"}>
              <div className="text-sm font-bold text-slate-900 tracking-tight">BRRADS</div>
              <div className="text-[11px] text-slate-500 font-medium">Inventory System</div>
            </div>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Tutup sidebar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Navigation */}
          <div className="sidebar-dark flex-1 overflow-y-auto px-3 py-3">
            {/* Access badge */}
            <div className={sidebarCollapsed ? "mb-3 rounded-lg border border-zinc-200 bg-white p-2 lg:flex lg:justify-center shadow-sm" : "mb-3 rounded-lg border border-zinc-200 bg-white p-2.5 shadow-sm"}>
              <div className={sidebarCollapsed ? "flex items-center gap-2.5 lg:justify-center" : "flex items-center gap-2.5"}>
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-600">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div className={sidebarCollapsed ? "min-w-0 lg:hidden" : "min-w-0"}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Access</div>
                  <div className="mt-0.5 truncate text-[12px] font-medium text-zinc-900">
                    {auth.isAdmin ? "Admin" : "Staff"}
                  </div>
                </div>
              </div>
            </div>

            {/* Nav sections */}
            <nav className="space-y-1">
              {sectionsWithIndex.map((section) => (
                <NavSection
                  key={section.title}
                  title={section.title}
                  items={section.items}
                  collapsed={sidebarCollapsed}
                  onItemClick={() => setMobileMenuOpen(false)}
                  startIndex={section.startIndex}
                />
              ))}
            </nav>
          </div>

          {/* User profile — always visible at bottom */}
          <div className="border-t border-zinc-200 p-3 bg-zinc-50">
            <div className={sidebarCollapsed ? "flex items-center gap-3 lg:flex-col lg:gap-2" : "flex items-center gap-3"}>
              {/* Avatar */}
              <div className="relative shrink-0">
                <div className="relative flex h-8 w-8 items-center justify-center rounded-md bg-zinc-900 text-xs font-medium text-white shadow-sm">
                  {String(auth.user?.username || "U").slice(0, 1).toUpperCase()}
                </div>
              </div>
              <div className={sidebarCollapsed ? "min-w-0 flex-1 lg:hidden" : "min-w-0 flex-1"}>
                <div className="truncate text-sm font-semibold text-slate-900">{auth.user?.username || "-"}</div>
                <div className="text-[11px] text-slate-500 capitalize">{auth.user?.role || "-"}</div>
              </div>
              <button
                onClick={onLogout}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-all hover:bg-slate-100 hover:text-rose-500 active:scale-95 " +
                  (sidebarCollapsed ? "" : "")
                }
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {mobileMenuOpen ? (
          <button
            type="button"
            aria-label="Tutup menu"
            className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        ) : null}

        {/* ─── MAIN CONTENT ─── */}
        <div className="relative flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
          <ShellHeader
            onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
            sidebarCollapsed={sidebarCollapsed}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            auth={auth}
            onLogout={onLogout}
          />

          <main key={pathname} className="min-h-0 flex-1 overflow-y-auto px-4 py-5 lg:px-6 lg:py-6 animate-page">
            <div className="mx-auto w-full max-w-[1520px]">{children}</div>
          </main>
        </div>
      </div>
    </div>
  );
}